import React, { useState, useEffect } from 'react';
import { User, TrendingUp, TrendingDown, ShoppingCart, QrCode, RefreshCw } from 'lucide-react';
import './PersonalArea.css';

interface Sale {
  id: number;
  drinkId: number;
  drinkName: string;
  quantity: number;
  priceAtSale: number;
  totalCost: number;
  timestamp: string;
  personId?: number;
}

interface Person {
  id: number;
  name: string;
  totalSpent: number;
  totalSavings: number;
  purchaseCount: number;
  createdAt: string;
  qrCode?: string;
  qrCodeData?: string;
}

interface PersonalAreaProps {
  isVisible: boolean;
  persons: Person[];
  salesHistory: Sale[];
}

const PersonalArea: React.FC<PersonalAreaProps> = ({ isVisible, persons, salesHistory }) => {
  const [person, setPerson] = useState<Person | null>(null);
  const [recentPurchases, setRecentPurchases] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (isVisible) {
      loadPersonalData();
    }
  }, [isVisible, persons, salesHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPersonalData = () => {
    const guestId = localStorage.getItem('guestId');
    if (!guestId) {
      setError('Keine Registrierung gefunden');
      return;
    }

    setLoading(true);
    try {
      // Find person data from props
      const currentPerson = persons.find((p: Person) => p.id.toString() === guestId);
      
      if (!currentPerson) {
        setError('Person nicht gefunden');
        return;
      }

      setPerson(currentPerson);

      // Filter sales data from props
      const personalSales = salesHistory
        .filter((sale: Sale) => sale.personId?.toString() === guestId)
        .sort((a: Sale, b: Sale) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10);
      setRecentPurchases(personalSales);

      setError('');
    } catch (error) {
      console.error('Error loading personal data:', error);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(2)}€`;
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSavingsColor = (savings: number) => {
    if (savings > 0) return '#00ff9f'; // Green for savings
    if (savings < 0) return '#ff5252'; // Red for losses
    return '#ffffff'; // White for neutral
  };

  const downloadQRCode = () => {
    if (person?.qrCode) {
      const link = document.createElement('a');
      link.download = `${person.name}-qr-code.png`;
      link.href = person.qrCode;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const clearPersonalData = () => {
    localStorage.removeItem('guestId');
    localStorage.removeItem('guestName');
    setPerson(null);
    setRecentPurchases([]);
    setError('');
  };

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <div className="personal-area">
        <div className="loading-state">
          <RefreshCw className="loading-icon spinning" />
          <p>Lade deine Daten...</p>
        </div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="personal-area">
        <div className="error-state">
          <h2>❌ Keine Daten</h2>
          <p>{error || 'Du bist noch nicht registriert'}</p>
          <button onClick={clearPersonalData} className="clear-btn">
            Gespeicherte Daten löschen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="personal-area">
      <div className="personal-header">
        <div className="user-info">
          <User className="user-icon" />
          <div>
            <h1>Hallo, {person.name}!</h1>
            <p>Dein persönlicher Bereich</p>
          </div>
        </div>
        <div className="header-actions">
          {person.qrCode && (
            <button onClick={() => setShowQR(!showQR)} className="qr-toggle-btn">
              <QrCode size={20} />
              {showQR ? 'QR ausblenden' : 'QR zeigen'}
            </button>
          )}
          <button onClick={loadPersonalData} className="refresh-btn">
            <RefreshCw size={20} />
            Aktualisieren
          </button>
        </div>
      </div>

      {showQR && person.qrCode && (
        <div className="qr-display">
          <div className="qr-container">
            <img 
              src={person.qrCode} 
              alt="Dein QR-Code" 
              className="qr-image"
            />
            <p>Dein persönlicher QR-Code</p>
            <button onClick={downloadQRCode} className="download-qr-btn">
              QR-Code speichern
            </button>
          </div>
        </div>
      )}

      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-header">
            <ShoppingCart className="stat-icon" />
            <h3>Gekaufte Getränke</h3>
          </div>
          <div className="stat-value">{person.purchaseCount}</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <TrendingUp className="stat-icon" />
            <h3>Ausgaben gesamt</h3>
          </div>
          <div className="stat-value">{formatCurrency(person.totalSpent)}</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            {person.totalSavings >= 0 ? 
              <TrendingUp className="stat-icon" style={{color: '#00ff9f'}} /> :
              <TrendingDown className="stat-icon" style={{color: '#ff5252'}} />
            }
            <h3>{person.totalSavings >= 0 ? 'Ersparnis' : 'Verlust'}</h3>
          </div>
          <div 
            className="stat-value" 
            style={{color: getSavingsColor(person.totalSavings)}}
          >
            {formatCurrency(Math.abs(person.totalSavings))}
          </div>
        </div>
      </div>

      {recentPurchases.length > 0 && (
        <div className="purchases-section">
          <h2>🛒 Deine letzten Käufe</h2>
          <div className="purchases-list">
            {recentPurchases.map((purchase) => (
              <div key={purchase.id} className="purchase-item">
                <div className="purchase-info">
                  <strong>{purchase.drinkName}</strong>
                  <span className="purchase-quantity">{purchase.quantity}x</span>
                </div>
                <div className="purchase-details">
                  <span className="purchase-price">{formatCurrency(purchase.totalCost)}</span>
                  <span className="purchase-time">{formatDateTime(purchase.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {person.purchaseCount === 0 && (
        <div className="empty-state">
          <ShoppingCart size={48} />
          <h3>Noch keine Käufe</h3>
          <p>Du hast noch nichts gekauft. Zeig deinen QR-Code an der Bar!</p>
        </div>
      )}

      <div className="data-management">
        <button onClick={clearPersonalData} className="clear-data-btn">
          Daten von diesem Gerät löschen
        </button>
        <p className="data-note">
          Deine Käufe bleiben im System gespeichert, aber werden auf diesem Gerät nicht mehr angezeigt.
        </p>
      </div>
    </div>
  );
};

export default PersonalArea;